import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ShippingMeanService } from '../../_services/shipping-mean.service';
import { CountryService } from '../../_services/country.service';
import { User } from '../../_models/user';
import { ShippingType } from '../../_models/shipping-type';

@Component({
    selector: 'app-shipping-means',
    templateUrl: './shipping-means.component.html',
    styleUrls: ['./shipping-means.component.css'],
    standalone: false
})
export class ShippingMeansComponent implements OnInit {
    shippingForm: FormGroup;
    shippingTypes: ShippingType[] = [];
    countries: any[] = [];
    user: User;
    loading = true;
    submitting = false;
    showForm = false;
    shippingOptions: any[] = [];
    selectedOption: any = null;
    isEditing = false;
    optionToDelete: any = null;
    showDeleteModal = false;

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private shippingMeanService: ShippingMeanService,
        private countryService: CountryService,
        private toastr: ToastrService,
        private translateService: TranslateService
    ) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        this.shippingForm = this.fb.group({
            shippingTypeId: ['', Validators.required],
            countryId: ['', Validators.required]
        });
    }

    ngOnInit(): void {
        this.loadData();
        this.loadShippingOptions();
    }

    loadData(): void {
        this.loading = true;
        this.shippingMeanService.getShippingTypes().subscribe({
            next: (types) => {
                this.shippingTypes = types;
                this.countryService.getCountries().subscribe({
                    next: (countries) => {
                        this.countries = countries;
                        this.setDefaultCountry();
                        this.loading = false;
                    },
                    error: () => {
                        this.toastr.error('Failed to load countries');
                        this.loading = false;
                    }
                });
            },
            error: () => {
                this.toastr.error('Failed to load shipping types');
                this.loading = false;
            }
        });
    }

    loadShippingOptions(): void {
        this.shippingMeanService.getShippingOptions().subscribe({
            next: (response: any) => {
                this.shippingOptions = response?.data || [];
            },
            error: () => {
                this.shippingOptions = [];
            }
        });
    }

    toggleForm(): void {
        if (this.showForm && this.isEditing) {
            this.cancelEdit();
        } else {
            this.showForm = !this.showForm;
        }
    }

    editOption(option: any): void {
        this.selectedOption = option;
        this.isEditing = true;
        this.showForm = true;
        this.shippingForm.patchValue({
            shippingTypeId: option.shippingType?.id || option.shippingTypeId,
            countryId: option.country?.id || option.countryId
        });
    }

    cancelEdit(): void {
        this.selectedOption = null;
        this.isEditing = false;
        this.showForm = false;
        this.shippingForm.reset();
        this.setDefaultCountry();
    }

    confirmDelete(option: any): void {
        this.optionToDelete = option;
        this.showDeleteModal = true;
    }

    closeDeleteModal(): void {
        this.optionToDelete = null;
        this.showDeleteModal = false;
    }

    deleteOption(): void {
        if (!this.optionToDelete) {
            return;
        }

        this.shippingMeanService.deleteShippingOption(this.optionToDelete.id).subscribe({
            next: () => {
                this.translateService.get('shipping_options.delete_success').subscribe(message => {
                    this.toastr.success(message);
                });
                this.closeDeleteModal();
                this.loadShippingOptions();
            },
            error: () => {
                this.translateService.get('shipping_options.delete_error').subscribe(message => {
                    this.toastr.error(message);
                });
                this.closeDeleteModal();
            }
        });
    }

    setDefaultCountry(): void {
        if (!this.user?.country || !this.countries?.length) {
            return;
        }
        
        const userCountryId = typeof this.user.country === 'object' ? this.user.country?.id : this.user.country;
        const userCountryName = typeof this.user.country === 'object' ? this.user.country?.name : this.user.countryName;
        
        const defaultCountry = this.countries.find(c => 
            c.id === userCountryId || c.name === userCountryName
        );
        
        if (defaultCountry) {
            this.shippingForm.get('countryId')?.setValue(defaultCountry.id);
        }
    }

    onSubmit(): void {
        if (this.shippingForm.invalid) {
            return;
        }

        this.submitting = true;
        const { shippingTypeId, countryId } = this.shippingForm.value;

        if (this.isEditing && this.selectedOption) {
            this.shippingMeanService.updateShippingOption(this.selectedOption.id, shippingTypeId, countryId).subscribe({
                next: () => {
                    this.translateService.get('shipping_options.update_success').subscribe(message => {
                        this.toastr.success(message);
                    });
                    this.cancelEdit();
                    this.submitting = false;
                    this.loadShippingOptions();
                },
                error: () => {
                    this.translateService.get('shipping_options.update_error').subscribe(message => {
                        this.toastr.error(message);
                    });
                    this.submitting = false;
                }
            });
        } else {
            this.shippingMeanService.addShippingOption(shippingTypeId, countryId).subscribe({
                next: (response: any) => {
                    this.translateService.get('shipping_options.success').subscribe(message => {
                        this.toastr.success(message);
                    });
                    const newOptionId = response?.data?.id || response?.id;
                    if (newOptionId) {
                        this.router.navigate(['/user/shipping-means', newOptionId]);
                    } else {
                        this.shippingForm.reset();
                        this.setDefaultCountry();
                        this.submitting = false;
                        this.showForm = false;
                        this.loadShippingOptions();
                    }
                },
                error: (err) => {
                    this.translateService.get('shipping_options.error').subscribe(message => {
                        this.toastr.error(message);
                    });
                    this.submitting = false;
                }
            });
        }
    }
}
